import React from 'react';
import UserAvatar from 'components/pure/utils/UserAvatar';
import DistributionTooltip from './DistributionTooltip';
import Theme from 'styles/theme';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import sum from 'lodash/sum';
import ManagerSparklineTooltip from 'components/dashboard/admin/manager-coaching/ManagerSparklineTooltip';
import CoachingEffectivenessScore from 'components/dashboard/admin/manager-coaching/CoachingEffectivenessScore';
import TrendRankCombo from 'components/dashboard/admin/TrendRankCombo';
import {formatHours} from 'components/dashboard/coaching-effectiveness/common';
import FeedbacksHoursListenedTooltip from 'components/dashboard/admin/manager-coaching/FeedbacksHoursListenedTooltip';
import {ManagerRespondedTooltip} from 'components/dashboard/admin/manager-coaching/ManagerRespondedTooltip';
import convertDateToUTC from 'utils/datetime/convertToMidnightUTC';
import {Sparkline} from 'charts';
import {MarkupSimpleProgressRing} from 'charts/components/charts/SimpleProgressRing';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import {getMuiV4Theme} from 'utils/css/themeUtils';

// 110 is from $feedbacksHoursListenedWidth of AdminDashCoachingActivity.styl,
// 40 is allowance for padding and the number following the bars
const feedbacksHoursListenedMaxWidth = 110 - 40;

/**
 * Generate a grand total of coaching time in the period given the two trends
 *
 * @param meetingActivity meeting times
 * @param reviewActivity review times
 * @return {number}
 */
export const totalForActivityRow = (meetingActivity, reviewActivity) => {
  return (
    sum(meetingActivity.data.map(d => d.value).filter(d => d)) +
    sum(reviewActivity.data.map(d => d.value).filter(d => d))
  );
};

export default class ManagerCoachingRow extends React.PureComponent {
  /**
   * based on the score, return the correct color class
   * @param score
   * @returns {string}
   */
  getScoreColor(score) {
    if (this.props.dateRangeTooSmall) return 'manager-activity-score-color-one';
    if (score < 0.75) return 'manager-activity-score-color-one';
    if (score < 1.25) return 'score-color-three';
    return 'score-color-five';
  }

  showCoachNA() {
    return (
      <div className="coach-na">
        <GenericTooltip
          positionTop
          tooltip={
            "This coach doesn't have a team of direct reports, so this metric is not applicable."
          }
          delay={TOOLTIP_DELAY}
          closeImmediately>
          N/A
        </GenericTooltip>
      </div>
    );
  }

  render() {
    const {
      directReportsOnly,
      managerCoachingActivity,
      fromPath,
      maxFeedbacksCount,
      maxHoursListened,
      showEffectivenessTag,
      startDate,
      endDate,
    } = this.props;
    const {
      manager,
      meetingActivity,
      reviewActivity,
      teamSize,
      membersCoachedCount,
      roundedAverage,
      scoredCount,
      requestsRespondedTo,
      totalRequests,
      coachingEffectivenessScore,
      activityRank,
      activityTrend,
      scoreRank,
      scoreTrend,
      commentsCount,
      highlightsCount,
      feedbacksCount,
      hoursListened,
      effTagLabel,
      effTagWorstOrBest,
    } = managerCoachingActivity;
    const isPureCoach = manager.isCoach && teamSize === 0;

    const activityData = [
      {data: meetingActivity.data, color: Theme.palette.dustyGray, timeType: 'Meeting'},
      {data: reviewActivity.data, color: getMuiV4Theme().palette.primary.main, timeType: 'Review'},
    ].map(s => ({
      ...s,
      color: s.color,
      data: s.data.map(i => ({
        date: convertDateToUTC(new Date(i.date)),
        value: i.value,
      })),
    }));

    // no activity at all? on either series?
    // otherwise if only one is empty, `bridge` will give us zeroes on the other
    const noActivity = !activityData
      .map(series => series.data)
      .flat()
      .some(d => d.value);

    const transformedData = activityData.map(s => ({
      ...s,
      data: s.data.map(d => ({...d, value: d.value === null ? 0 : d.value})),
    }));

    const distribution = teamSize > 0 ? (membersCoachedCount / teamSize) * 100 : 0;

    const tinyAmountForZero = 0.02;
    const feedbacksOutOfMaxRatio = Math.max(feedbacksCount / maxFeedbacksCount, tinyAmountForZero);
    const hoursListenedOutOfMaxRatio = Math.max(
      hoursListened / maxHoursListened,
      tinyAmountForZero
    );

    // Shorten the max width of the bars if the highest number is 5-digit or more
    const maxWidth =
      Math.max(maxFeedbacksCount, maxHoursListened) < 10000
        ? feedbacksHoursListenedMaxWidth
        : feedbacksHoursListenedMaxWidth - 20;

    const feedbackWidth = Math.round(maxWidth * feedbacksOutOfMaxRatio);
    const hoursWidth = Math.round(maxWidth * hoursListenedOutOfMaxRatio);

    const feedbacksHoursListenedData = {
      owner: manager.getDisplayName(),
      scoredCount,
      commentsCount,
      highlightsCount,
      hoursListened,
    };

    const from = convertDateToUTC(new Date(reviewActivity.data[0].date));
    const to = convertDateToUTC(new Date(reviewActivity.data[reviewActivity.data.length - 1].date));

    const noActivityMessage = directReportsOnly ? 'No Team Coaching' : 'No Coaching Activity';

    return (
      <div className="activity-row">
        <div className="activity-details">
          {showEffectivenessTag && effTagLabel && (
            <div className={`best-worst-label`}>
              <div className={`label ${effTagWorstOrBest}`}>{effTagLabel}</div>
              <div className={`arrow ${effTagWorstOrBest}`} />
            </div>
          )}
          <div
            className="manager-effectiveness pointer"
            onClick={() => this.props.managerNameClick(manager)}>
            <div className="avatar-name">
              <UserAvatar user={manager} />
              <div className="full-name">
                <span
                  // This style needs to be applied here to avoid being lost in
                  // styl transpilation
                  // https://github.com/postcss/autoprefixer/issues/1141
                  style={{WebkitBoxOrient: 'vertical'}}>
                  {manager.firstName}
                </span>
                <span
                  // This style needs to be applied here to avoid being lost in
                  // styl transpilation
                  // https://github.com/postcss/autoprefixer/issues/1141
                  style={{WebkitBoxOrient: 'vertical'}}>
                  {manager.lastName}
                </span>
              </div>
            </div>
            <div className="coaching-effectiveness">
              <CoachingEffectivenessScore
                score={coachingEffectivenessScore}
                manager={manager}
                startDate={startDate}
                endDate={endDate}
                currentPeriodScorecardTrend={scoreTrend.trend}
                currentPeriodActivityTrend={activityTrend.trend}
              />
            </div>
          </div>
          <div className="manager-activity">
            <div className="sparkline-wrapper">
              {noActivity ? (
                <p>{noActivityMessage}</p>
              ) : (
                <GenericTooltip
                  tooltip={
                    <ManagerSparklineTooltip
                      fromPath={fromPath}
                      managerId={manager.id}
                      adminId={this.props.adminId}
                      from={from}
                      to={to}
                      series={transformedData}
                      meetingTotalInSeconds={sum(
                        meetingActivity.data.map(d => d.value).filter(d => d)
                      )}
                      reviewTotalInSeconds={sum(
                        reviewActivity.data.map(d => d.value).filter(d => d)
                      )}
                    />
                  }
                  positionTop
                  delay={TOOLTIP_DELAY}
                  className="manager-sparkline-tooltip-container">
                  <Sparkline from={from} to={to} series={transformedData} />
                </GenericTooltip>
              )}
            </div>
          </div>
          <div className="manager-distribution">
            {isPureCoach ? (
              this.showCoachNA()
            ) : (
              <GenericTooltip
                delay={TOOLTIP_DELAY}
                positionTop
                closeImmediately
                tooltip={
                  <DistributionTooltip
                    user={manager}
                    teamSize={teamSize}
                    membersCoached={membersCoachedCount}
                  />
                }>
                <div className="donut distribution">
                  <MarkupSimpleProgressRing
                    size={70}
                    progress={distribution}
                    backgroundColor={'#CBD0D6'}
                    highlightColor={getMuiV4Theme().palette.primary.main}
                    includePlusMinus={false}
                  />
                </div>
              </GenericTooltip>
            )}
          </div>
          <div className="manager-responded">
            {isPureCoach ? (
              this.showCoachNA()
            ) : (
              <GenericTooltip
                delay={TOOLTIP_DELAY}
                positionTop
                closeImmediately
                tooltip={
                  <ManagerRespondedTooltip
                    totalRequests={totalRequests}
                    requestsRespondedTo={requestsRespondedTo}
                    requestPercent={distribution}
                    name={manager.firstName}
                    includePlusMinus={false}
                  />
                }>
                <div className="donut distribution">
                  <MarkupSimpleProgressRing
                    size={70}
                    progress={
                      totalRequests ? (requestsRespondedTo / totalRequests) * 100 : undefined
                    }
                    backgroundColor={'#CBD0D6'}
                    highlightColor={getMuiV4Theme().palette.primary.main}
                    includePlusMinus={false}
                  />
                </div>
              </GenericTooltip>
            )}
          </div>
          <div className="feedbacks-hours-listened">
            <GenericTooltip
              tooltip={<FeedbacksHoursListenedTooltip {...feedbacksHoursListenedData} />}
              positionTop
              delay={TOOLTIP_DELAY}
              className="feedbacks-hours-listened-tooltip-container"
              closeImmediately>
              <div className="row">
                <div className="bar feedbacks" style={{width: feedbackWidth}} />
                {feedbacksCount}
              </div>
            </GenericTooltip>
            <GenericTooltip
              tooltip={<FeedbacksHoursListenedTooltip {...feedbacksHoursListenedData} />}
              positionBottom
              delay={TOOLTIP_DELAY}
              className="feedbacks-hours-listened-tooltip-container"
              closeImmediately>
              <div className="row">
                <div className="bar hours-listened" style={{width: hoursWidth}} />
                {formatHours(hoursListened)}
              </div>
            </GenericTooltip>
          </div>
          <div className="manager-score-trend-rank">
            {isPureCoach ? (
              this.showCoachNA()
            ) : (
              <TrendRankCombo {...this.props} rank={scoreRank} trend={scoreTrend} />
            )}
          </div>
          <div className="manager-score-count">
            <GenericTooltip
              positionTop
              delay={TOOLTIP_DELAY}
              closeImmediately
              tooltip={
                <div>
                  <p>
                    {manager.firstName} scored {scoredCount} calls
                    {isPureCoach
                      ? '.'
                      : ` for members of their team during this period with an average of ${roundedAverage} calls scored per rep each week.`}
                  </p>
                  {!isPureCoach && (
                    <p>
                      The color scale is based on the average number of calls scored, per rep,
                      across the manager's team each week. Gray indicates an average of 0 or near
                      zero; yellow indicates an average between 0.5 and 1.5; green indicates an
                      average greater than 1.5. We recommend that managers aim to score around 2
                      calls per rep per week.
                    </p>
                  )}
                </div>
              }>
              <div className={'score-block ' + this.getScoreColor(roundedAverage)}>
                <span>{scoredCount}</span>
              </div>
            </GenericTooltip>
          </div>
          <div className="manager-activity-trend-rank">
            {isPureCoach ? (
              this.showCoachNA()
            ) : (
              <TrendRankCombo {...this.props} rank={activityRank} trend={activityTrend} />
            )}
          </div>
        </div>
      </div>
    );
  }
}
